import { Button, Modal, Text, Group, Loader, Stack, Alert } from '@mantine/core';
import { IconDiscountCheckFilled } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useGetRedeemPointsPreview } from '../../../hooks/orders/mutations';
import { formatMoneyString } from '../../../utils/strings';
import { trackEvent } from '../../../utils/monitoring/posthog';
import { MonitoringEventTypes } from '../../../utils/monitoring/Monitoring_Events_Enum';
import { usePostHog } from 'posthog-js/react';

interface Props {
  opened: boolean;
  onClose: () => void;
  customerId: string;
  storeId: string;
  orderTotal: number;
  onConfirm: (data: { discount: number; pointsUsed: number }) => void;
}

export const RedeemPointsModal = ({
  opened,
  onClose,
  customerId,
  storeId,
  orderTotal,
  onConfirm
}: Props) => {
  const posthog = usePostHog();
  const [redeemData, setRedeemData] = useState<{
    discount: number;
    pointsUsed: number;
  } | null>(null);

  const { mutateAsync, isPending } = useGetRedeemPointsPreview();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await mutateAsync({ customerId, storeId, orderTotal });
        setRedeemData(data || null);
      } catch (error) {
        console.error('Error al obtener vista previa de puntos', error);
        setRedeemData(null);
      }
    };

    if (opened) {
      fetchData();
    } else {
      setRedeemData(null);
    }

    trackEvent(posthog, MonitoringEventTypes.LOYALTY_POINTS_PRESENTED, {
      storeId,
      customerId,
      orderTotal
    });
  }, [opened, customerId, storeId, orderTotal]);

  return (
    <Modal opened={opened} onClose={onClose} title="Redimir puntos" centered>
      {isPending ? (
        <Loader />
      ) : redeemData ? (
        <Stack>
          <Text>
            Este cliente puede usar <strong>{redeemData.pointsUsed}</strong> puntos para obtener un
            descuento de <strong>{formatMoneyString(redeemData.discount)}</strong>.
          </Text>

          <Alert icon={<IconDiscountCheckFilled />} title="Importante" color="blue">
            Una vez confirmado, los puntos serán descontados y el pedido incluirá este descuento.
          </Alert>

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                onConfirm(redeemData);
                onClose();
                trackEvent(posthog, MonitoringEventTypes.LOYALTY_POINTS_APPLIED, {
                  storeId,
                  customerId,
                  orderTotal,
                  discount: redeemData.discount,
                  pointsUsed: redeemData.pointsUsed
                });
              }}
              color="blue">
              Aplicar puntos
            </Button>
          </Group>
        </Stack>
      ) : (
        <Text>No hay puntos disponibles para redimir en este pedido.</Text>
      )}
    </Modal>
  );
};
